import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Stethoscope, UploadSimple } from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import PasswordInput from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail, saveAuthTokens } from "@/lib/api";
import SpecialtySelect from "@/components/SpecialtySelect";
import LocationSelect from "@/components/LocationSelect";
import TimeSelect from "@/components/TimeSelect";
import Footer from "@/components/Footer";
import SlotScheduleDesigner from "@/components/SlotScheduleDesigner";

const ALL_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function ApplyAsDoctor() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const defaultSchedule = () => {
    const s = {};
    ALL_DAYS.forEach((d) => { s[d] = { enabled: d !== "Sun", start_time: "09:00", end_time: "19:00", sessions: [{ id: "default", label: "Clinic", start_time: "09:00", end_time: "19:00", max_appointments: 0 }] }; });
    return s;
  };
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    photo_url: "",
    specialty: "",
    qualifications: "",
    years_experience: 0,
    clinic_name: "",
    clinic_address: "",
    consultation_fee: 0,
    max_daily_appointments: 30,
    bio: "",
    schedule: defaultSchedule(),
    show_experience: true,
    show_fee: true,
    state: "",
    district: "",
    booking_mode: "token_based",
    slot_duration_minutes: 60,
    slot_buffer_minutes: 0,
    max_patients_per_slot: 1,
    slot_booking_cutoff_minutes: 0,

  });

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const updateSchedule = (day, field, value) =>
    setForm((s) => ({ ...s, schedule: { ...s.schedule, [day]: { ...s.schedule[day], [field]: value } } }));
  const replaceDaySchedule = (day, nextDaySchedule) =>
    setForm((s) => ({ ...s, schedule: { ...s.schedule, [day]: nextDaySchedule } }));
  const sessionsFor = (day) => {
    const s = form.schedule[day] || {};
    return s.sessions?.length ? s.sessions : [{ id: "default", label: "Clinic", start_time: s.start_time || "09:00", end_time: s.end_time || "19:00", max_appointments: 0 }];
  };
  const updateSession = (day, index, patch) => {
    setForm((state) => {
      const daySchedule = state.schedule[day] || {};
      const base = daySchedule.sessions?.length ? daySchedule.sessions : [{ id: "default", label: "Clinic", start_time: daySchedule.start_time || "09:00", end_time: daySchedule.end_time || "19:00", max_appointments: 0 }];
      const sessions = base.map((session, i) => (i === index ? { ...session, ...patch } : session));
      return { ...state, schedule: { ...state.schedule, [day]: { ...daySchedule, sessions, start_time: sessions[0].start_time, end_time: sessions[sessions.length - 1].end_time } } };
    });
  };
  const addSession = (day) => setForm((state) => {
    const daySchedule = state.schedule[day] || {};
    const base = daySchedule.sessions?.length ? daySchedule.sessions : [{ id: "default", label: "Clinic", start_time: daySchedule.start_time || "09:00", end_time: daySchedule.end_time || "19:00", max_appointments: 0 }];
    const nextIndex = base.length + 1;
    const sessions = [...base, { id: `session_${nextIndex}`, label: nextIndex === 2 ? "Evening" : `Session ${nextIndex}`, start_time: "17:00", end_time: "20:00", max_appointments: 0 }];
    return { ...state, schedule: { ...state.schedule, [day]: { ...daySchedule, enabled: true, sessions, end_time: "20:00" } } };
  });
  const removeSession = (day, index) => setForm((state) => {
    const daySchedule = state.schedule[day] || {};
    const sessions = (daySchedule.sessions || []).filter((_, i) => i !== index);
    const next = sessions.length ? sessions : [{ id: "default", label: "Clinic", start_time: "09:00", end_time: "19:00", max_appointments: 0 }];
    return { ...state, schedule: { ...state.schedule, [day]: { ...daySchedule, sessions: next, start_time: next[0].start_time, end_time: next[next.length - 1].end_time } } };
  });
  const choosePhoto = (file) => {
    setPhotoFile(file || null);
    setPhotoPreview(file ? URL.createObjectURL(file) : "");
    set("photo_url", "");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (!photoFile) {
        toast.error("Please upload a professional headshot.");
        return;
      }

      const uploadData = new FormData();
      uploadData.append("file", photoFile);
      const uploaded = await api.post("/uploads/doctor-photo", uploadData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const enabledDays = ALL_DAYS.filter((d) => form.schedule[d]?.enabled);
      const isSlotBased = form.booking_mode === "slot_based";
      const applied = await api.post("/doctors/apply", {
        ...form,
        photo_url: uploaded.data.photo_url,
        working_days: enabledDays,
        working_schedule: form.schedule,
        start_time: enabledDays.length ? form.schedule[enabledDays[0]].start_time : "09:00",
        end_time: enabledDays.length ? form.schedule[enabledDays[0]].end_time : "19:00",
        years_experience: Number(form.years_experience),
        consultation_fee: Number(form.consultation_fee),
        max_daily_appointments: Number(form.max_daily_appointments),
        show_experience: form.show_experience,
        show_fee: form.show_fee,
        state: form.state || undefined,
        district: form.district || undefined,
        booking_mode: form.booking_mode,
        slot_duration_minutes: isSlotBased ? Number(form.slot_duration_minutes) || 60 : undefined,
        slot_buffer_minutes: isSlotBased ? Number(form.slot_buffer_minutes) || 0 : undefined,
        max_patients_per_slot: isSlotBased ? Number(form.max_patients_per_slot) || 1 : undefined,
        slot_booking_cutoff_minutes: isSlotBased ? Number(form.slot_booking_cutoff_minutes) || 0 : undefined,
      });
      saveAuthTokens(applied.data?.token, applied.data?.refresh_token);
      toast.success("Application submitted! Awaiting admin approval.");
      await refresh();
      nav("/doctor-pending");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="apply-page">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">For doctors</div>
        <h1 className="font-display font-bold text-foreground text-3xl sm:text-4xl lg:text-5xl tracking-tight">
          Join the platform
        </h1>
        <p className="mt-3 text-muted-foreground max-w-xl text-sm sm:text-base">
          Tell patients about your practice. Your profile becomes live after a quick admin review.
        </p>

        <form onSubmit={submit} className="mt-8 sm:mt-10 space-y-6 sm:space-y-8" data-testid="apply-form">
          <Section title="Account">
            <Field label="Full name" required>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} required minLength={2} className="h-12" data-testid="apply-name" />
            </Field>
            <Field label="Email" required>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="h-12" data-testid="apply-email" />
            </Field>
            <Field label="Password" required>
              <PasswordInput value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={8} className="h-12" data-testid="apply-password" />
            </Field>
            <Field label="Phone" required>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} required className="h-12" data-testid="apply-phone" />
            </Field>
          </Section>

          <Section title="Profile" subtitle="Help patients trust your credentials.">
            <Field label="Profile photo" required hint="Upload a clear professional headshot. JPG, PNG, or WebP works best.">
              <div className="flex flex-col sm:flex-row gap-4">
                <label className="flex-1 min-h-28 rounded-2xl border border-dashed border-border bg-background hover:bg-muted/50 transition-colors cursor-pointer px-4 py-5 flex items-center justify-center text-center">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/jpg"
                    onChange={(e) => choosePhoto(e.target.files?.[0])}
                    required
                    className="sr-only"
                    data-testid="apply-photo"
                  />
                  <span className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                    <UploadSimple size={24} weight="duotone" className="text-primary" />
                    <span className="font-medium text-foreground">{photoFile ? photoFile.name : "Choose photo"}</span>
                    <span>Click to upload from your device</span>
                  </span>
                </label>
                {photoPreview && (
                  <img
                    src={photoPreview}
                    alt="Selected doctor profile"
                    className="h-28 w-28 rounded-2xl object-cover border border-border"
                  />
                )}
              </div>
            </Field>
            <Field label="Specialty" required>
              <SpecialtySelect value={form.specialty} onChange={(v) => set("specialty", v)} />
            </Field>
            <Field label="Qualifications" required>
              <Input value={form.qualifications} onChange={(e) => set("qualifications", e.target.value)} required placeholder="e.g. MBBS, MD (Cardiology)" className="h-12" data-testid="apply-qualifications" />
            </Field>
            <Field label="Years of experience" required>
              <Input
                type="number"
                min={0}
                max={80}
                value={form.years_experience}
                onChange={(e) => set("years_experience", e.target.value)}
                required
                className="h-12"
                data-testid="apply-years"
              />
            </Field>
            <Field label="Short bio">
              <Textarea
                rows={3}
                maxLength={600}
                value={form.bio}
                onChange={(e) => set("bio", e.target.value)}
                placeholder="A brief intro about your practice and approach"
                className="resize-none"
                data-testid="apply-bio"
              />
            </Field>
            <div className="space-y-3 pt-2">
              <ToggleSwitch label="Show experience on listing" checked={form.show_experience} onChange={(v) => set("show_experience", v)} />
              <ToggleSwitch label="Show consultation fee on listing" checked={form.show_fee} onChange={(v) => set("show_fee", v)} />
            </div>
          </Section>

          <Section title="Clinic">
            <Field label="Clinic / hospital name" required>
              <Input value={form.clinic_name} onChange={(e) => set("clinic_name", e.target.value)} required className="h-12" data-testid="apply-clinic-name" />
            </Field>
            <Field label="Address" required>
              <Input value={form.clinic_address} onChange={(e) => set("clinic_address", e.target.value)} required className="h-12" data-testid="apply-clinic-address" />
            </Field>
            <LocationSelect
              state={form.state}
              district={form.district}
              onStateChange={(v) => { set("state", v); set("district", ""); }}
              onDistrictChange={(v) => set("district", v)}
              stateRequired
              districtRequired
              label="Location"
            />
            <Field label="Consultation fee (₹)" required>
              <Input
                type="number"
                min={0}
                step={50}
                value={form.consultation_fee}
                onChange={(e) => set("consultation_fee", e.target.value)}
                required
                className="h-12"
                data-testid="apply-fee"
              />
            </Field>
            <Field label="Bookings accepted per day" required hint="Patients cannot book once this daily limit is reached.">
              <Input
                type="number"
                min={1}
                max={500}
                value={form.max_daily_appointments}
                onChange={(e) => set("max_daily_appointments", e.target.value)}
                required
                className="h-12"
                data-testid="apply-daily-limit"
              />
            </Field>

          </Section>

          <Section title="Booking Style">
            <p className="text-sm text-muted-foreground -mt-2">Choose how patients book time with you.</p>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <button
                type="button"
                onClick={() => set("booking_mode", "token_based")}
                className={`rounded-2xl border p-4 text-left transition-all ${form.booking_mode === "token_based" ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border hover:border-primary/30"}`}
              >
                <span className="font-display font-bold text-foreground text-sm">Token Based</span>
                <p className="text-xs text-muted-foreground mt-1">Patients get a queue token and are served FCFS within each session.</p>
              </button>
              <button
                type="button"
                onClick={() => set("booking_mode", "slot_based")}
                className={`rounded-2xl border p-4 text-left transition-all ${form.booking_mode === "slot_based" ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border hover:border-primary/30"}`}
              >
                <span className="font-display font-bold text-foreground text-sm">Slot Based</span>
                <p className="text-xs text-muted-foreground mt-1">Patients book a fixed time slot. Great for appointment-style clinics.</p>
              </button>
            </div>
            {form.booking_mode === "slot_based" && (
              <div className="mt-4 rounded-2xl border border-primary/20 bg-primary/[0.02] p-4">
                <Field label="Close booking before slot" hint="Minutes before the appointment time. Use 0 to allow until slot time.">
                  <Input type="number" min={0} max={1440} step={5} value={form.slot_booking_cutoff_minutes} onChange={(e) => set("slot_booking_cutoff_minutes", e.target.value)} className="h-10" />
                </Field>
                <p className="text-xs text-muted-foreground mt-3">Set each session's time and slot length below. Slots appear automatically and you can remove the ones you do not want.</p>
              </div>
            )}
          </Section>

          <Section title={form.booking_mode === "slot_based" ? "Slot Timings" : "Token Timings"}>
            {form.booking_mode === "slot_based" ? (
              <SlotScheduleDesigner
                schedule={form.schedule}
                onChange={replaceDaySchedule}
                slotDuration={form.slot_duration_minutes}
                onSlotDurationChange={(value) => set("slot_duration_minutes", value)}
              />
            ) : (
              <TokenScheduleEditor
                schedule={form.schedule}
                updateSchedule={updateSchedule}
                updateSession={updateSession}
                addSession={addSession}
                removeSession={removeSession}
                sessionsFor={sessionsFor}
              />
            )}
          </Section>

          <div className="flex items-center gap-4 pt-4">
            <Button
              type="submit"
              disabled={busy}
              className="rounded-full h-12 px-7 bg-primary text-primary-foreground hover:bg-[#1a3025]"
              data-testid="apply-submit"
            >
              {busy ? "Submitting…" : "Submit application"}
            </Button>
            <Link to="/login" className="text-sm text-muted-foreground underline underline-offset-4">
              Already have a doctor account? Sign in
            </Link>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="rounded-3xl border border-border bg-card p-5 sm:p-8">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center text-primary">
          <Stethoscope size={20} weight="duotone" />
        </div>
        <div>
          <h2 className="font-display font-bold text-xl sm:text-2xl tracking-tight text-foreground">{title}</h2>
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <Label>
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      <div className="mt-2">{children}</div>
      {hint && <p className="text-xs text-muted-foreground mt-1.5">{hint}</p>}
    </div>
  );
}

function TokenScheduleEditor({ schedule, updateSchedule, updateSession, addSession, removeSession, sessionsFor }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Set open days and consultation sessions. Patients receive queue tokens within these timings.
      </p>
      {ALL_DAYS.map((day) => {
        const daySchedule = schedule[day] || {};
        const sessions = sessionsFor(day);
        return (
          <div key={day} className={`rounded-2xl border p-4 transition-colors ${daySchedule.enabled ? "border-primary/30 bg-primary/[0.02]" : "border-border bg-background"}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-display font-bold text-foreground">{day}</div>
                <div className="text-xs text-muted-foreground">{daySchedule.enabled ? `${sessions.length} session${sessions.length !== 1 ? "s" : ""}` : "Closed"}</div>
              </div>
              <ToggleSwitch label="" checked={!!daySchedule.enabled} onChange={(v) => updateSchedule(day, "enabled", v)} />
            </div>
            {daySchedule.enabled && (
              <div className="mt-4 space-y-3">
                {sessions.map((session, index) => (
                  <div key={`${day}-${session.id}-${index}`} className="rounded-xl border border-border bg-card p-3">
                    <div className="grid gap-3 sm:grid-cols-[1.2fr_1fr_1fr_0.9fr_auto] sm:items-end">
                      <div>
                        <Label className="text-xs">Session</Label>
                        <Input value={session.label || ""} onChange={(e) => updateSession(day, index, { label: e.target.value })} className="h-10 mt-1" placeholder="Morning" />
                      </div>
                      <div>
                        <Label className="text-xs">Start</Label>
                        <div className="mt-1"><TimeSelect value={session.start_time || "09:00"} onChange={(v) => updateSession(day, index, { start_time: v })} /></div>
                      </div>
                      <div>
                        <Label className="text-xs">End</Label>
                        <div className="mt-1"><TimeSelect value={session.end_time || "19:00"} onChange={(v) => updateSession(day, index, { end_time: v })} /></div>
                      </div>
                      <div>
                        <Label className="text-xs">Max tokens</Label>
                        <Input
                          type="number"
                          min={0}
                          value={session.max_appointments || ""}
                          onChange={(e) => updateSession(day, index, { max_appointments: Number(e.target.value) || 0 })}
                          placeholder="No cap"
                          className="h-10 mt-1"
                        />
                      </div>
                      {sessions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSession(day, index)}
                          className="h-10 rounded-full bg-destructive/10 px-3 text-xs font-semibold text-destructive hover:bg-destructive/15"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addSession(day)}
                  className="w-full rounded-full border border-dashed border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/5"
                >
                  Add another session
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ToggleSwitch({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer">
      <span className="text-sm text-foreground font-medium">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors ${checked ? "bg-primary" : "bg-input"}`}
      >
        <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0"}`} />
      </button>
    </label>
  );
}
